import { useEffect } from "react";
import { toast } from "sonner";
import { Database } from "lucide-react";
import { Button } from "./ui/button";
import { api, errMsg, fmtDT } from "../lib/api";
import { useLoad } from "./common";

const LABEL = {
  copying: "Copying everything over — reps keep pulling", catching_up: "Copy done — catching up the last changes",
  switching: "Switching over — pulls paused for about a minute", switched: "Done — the app now runs on your database",
  failed: "Stopped with an error", cancelled: "Cancelled",
};

export default function DbMovePanel() {
  const [d, reload] = useLoad(async () => (await api.get("/admin/db-move")).data);
  const st = d?.state || {};
  const live = ["copying", "catching_up", "switching"].includes(st.status);
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(reload, 5000);
    return () => { clearInterval(t); };
  }, [live]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const act = async (a) => {
    if (a === "start" && !window.confirm("Copy the whole app to your own database and switch over automatically when done? Reps keep pulling during the copy; pulls pause for about a minute at the switch.")) return;
    try { await api.post(`/admin/db-move/${a}`, {}); toast.success(a === "start" ? "Move started" : "Move cancelled"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const colls = Object.entries(st.colls || {});
  const leads = st.colls?.leads?.copied || 0;
  const leadsTotal = st.totals?.leads || 0;
  return (
    <div className="panel p-5 space-y-3" data-testid="db-move-panel">
      <h2 className="font-bold flex items-center gap-2"><Database className="w-4 h-4" />Move to your own database</h2>
      {!d.configured ? <p className="text-sm text-zinc-600" data-testid="db-move-not-configured">Your new database connection hasn't been added yet.</p>
        : <p className="text-sm" data-testid="db-move-target">New database: {d.target_ok ? <b className="text-emerald-700">reachable</b> : <b className="text-rose-700">can't connect — {d.target_error}</b>}{d.using_new && " · this server is using it"}</p>}
      {st.status && <div className="text-sm space-y-1" data-testid="db-move-status">
        <div><b>{LABEL[st.status] || st.status}</b>{st.error && <span className="text-rose-700"> — {st.error}</span>}</div>
        {leadsTotal > 0 && <div data-testid="db-move-leads-progress">Leads copied: <b>{leads.toLocaleString()}</b> of about {leadsTotal.toLocaleString()} ({Math.min(100, Math.round((leads / leadsTotal) * 100))}%)</div>}
        <div className="text-[11px] text-zinc-500">{colls.filter(([, c]) => c.done).length} of {Object.keys(st.totals || {}).length || "…"} collections done · {(st.tail_events || 0).toLocaleString()} live changes copied · started {fmtDT(st.started_at)}{st.switched_at ? ` · switched ${fmtDT(st.switched_at)}` : ""}</div>
      </div>}
      <div className="flex gap-2">
        {d.configured && d.target_ok && !live && st.status !== "switched" && <Button onClick={() => act("start")} data-testid="db-move-start-btn">{["failed", "cancelled"].includes(st.status) ? "Resume move" : "Start move"}</Button>}
        {["copying", "catching_up"].includes(st.status) && <Button variant="outline" onClick={() => act("cancel")} data-testid="db-move-cancel-btn">Cancel</Button>}
      </div>
    </div>
  );
}
